/* eslint-disable operator-linebreak */
import { useCallback, useEffect, useRef, useState } from 'react';
import * as ApiModels from '@/typings/questionnaires-api-models';
import * as Sentry from '@sentry/nextjs';
import {
  getQuestionnaireFieldTemplateReplicationIdentifier,
  getQuestionnaireResponseReplicationIdentifier,
  getQuestionnaireTemplateReplicationIdentifier,
  getReplicationDataByIdentifier,
} from '../rxdb.replication';
import { REPLICATION_INFO_STATUS } from '../rxdb.constants';
import { RxNoodleDBCollectionsKeys } from '../rxdb.types';
import { rxdbLog } from '../rxdb.tools';

const CHECK_INTERVAL = 10000;

// It's important that this number is higher than the CHECK_INTERVAL
// as we want to check the questionnaire requests and see if the timeout interval can be canceled (check the checkVerifiedData function)
const LOAD_TIMEOUT = 30000;

enum QuestionnaireCollections {
  QUESTIONNAIRE_TEMPLATE = 'questionnaireTemplate',
  QUESTIONNAIRE_FIELD_TEMPLATE = 'questionnaireFieldTemplate',
  QUESTIONNAIRE_RESPONSE = 'questionnaireResponse',
}

type Errors = {
  [key in QuestionnaireCollections]?: boolean;
};

type SuccessfullyLoaded = {
  [key in QuestionnaireCollections]?: boolean;
};

const useCheckErrorsInQuestionnaireRequests = ({
  questionnaireId,
  questionnaireTemplateId,
  questionnaireTemplate,
  questionnaireFieldTemplates,
  questionnaireResponses,
}: {
  questionnaireTemplate: false | ApiModels.QuestionnaireTemplateNoFields | null;
  questionnaireFieldTemplates: false | ApiModels.FieldTemplateItem[] | null;
  questionnaireResponses: false | ApiModels.QuestionnaireResponse[] | null;
  questionnaireId: string;
  questionnaireTemplateId: string | undefined;
}): { errors: Errors; successfullyLoaded: SuccessfullyLoaded } => {
  const intervalTimer = useRef<NodeJS.Timeout | null>(null);
  const intervalCanceled = useRef<boolean>(false);
  const loadTimeoutTimer = useRef<NodeJS.Timeout | null>(null);

  const [successfullyLoaded, setSuccessfullyLoaded] = useState<SuccessfullyLoaded>({});
  const [errors, setErrors] = useState<Errors>({});

  const reportTimeoutError = useRef<null | (() => void)>(null);

  reportTimeoutError.current = () => {
    const extra = {
      errors,
      questionnaireId,
      questionnaireTemplateId,
      successfullyLoaded,
    };

    rxdbLog('useCheckErrorsInQuestionnaireRequests - load timeout reached', extra);
    Sentry.captureException('useCheckErrorsInQuestionnaireRequests - load timeout reached', { extra });
  };

  const throwError = useCallback((error: string, collection: RxNoodleDBCollectionsKeys) => {
    Sentry.captureException(error, { extra: { collection } });
    rxdbLog(`useCheckErrorsInQuestionnaireRequests - couldn't load data correctly for ${collection} collection.`);
    setErrors(prevErrors => ({ ...prevErrors, [collection]: true }));
    setSuccessfullyLoaded(prevSuccessfullyLoaded => ({ ...prevSuccessfullyLoaded, [collection]: false }));
  }, []);

  const cancelInterval = useCallback((): void => {
    intervalCanceled.current = true;
    if (intervalTimer.current) {
      clearInterval(intervalTimer.current);
      intervalTimer.current = null;
    }
  }, []);

  const markAsFound = useCallback((data: unknown, collection: RxNoodleDBCollectionsKeys): void => {
    rxdbLog(`useCheckErrorsInQuestionnaireRequests - ${collection} collection found`, data);
    setSuccessfullyLoaded(prevSuccessfullyLoaded => ({ ...prevSuccessfullyLoaded, [collection]: true }));
    setErrors(prevErrors => ({ ...prevErrors, [collection]: false }));
  }, []);

  /**
   * Check if the questionnaire template is set after the given interval
   * If the replication is completed but the questionnaire template is not set, it will throw an error
   * If the replication is not active, it will throw an error
   */
  const checkQuestionnaireTemplate = useCallback(async (): Promise<void> => {
    if (questionnaireTemplate) {
      markAsFound(questionnaireTemplate, 'questionnaireTemplate');
      return;
    }

    const replicationIdentifier = getQuestionnaireTemplateReplicationIdentifier();
    const replicationData = await getReplicationDataByIdentifier(replicationIdentifier);
    const status = replicationData?.info.status;

    const shouldThrowError =
      !replicationData || // Replication data is not set
      (status === REPLICATION_INFO_STATUS.COMPLETED && !questionnaireTemplate) || // Replication is completed but questionnaire template is not set
      status !== REPLICATION_INFO_STATUS.ACTIVE; // Replication is not active

    if (shouldThrowError) throwError('Questionnaire template not found', 'questionnaireTemplate');
  }, [questionnaireTemplate, markAsFound, throwError]);

  /**
   * Check if the questionnaire field templates are set after the given interval
   * If the replication is not active or completed, it will throw an error
   * and the replication is completed
   */
  const checkQuestionnaireFieldTemplates = useCallback(async (): Promise<void> => {
    if (questionnaireFieldTemplates === false) return;
    if (questionnaireFieldTemplates?.length) {
      markAsFound(questionnaireFieldTemplates, 'questionnaireFieldTemplate');
      return;
    }

    const replicationIdentifier = getQuestionnaireFieldTemplateReplicationIdentifier(questionnaireTemplateId!);
    const replicationData = await getReplicationDataByIdentifier(replicationIdentifier);
    const status = replicationData?.info.status;

    const shouldThrowError =
      !replicationData || // Replication data is not set
      (status === REPLICATION_INFO_STATUS.COMPLETED && questionnaireFieldTemplates?.length === 0) || // Replication is completed but questionnaire field templates are not set
      status !== REPLICATION_INFO_STATUS.ACTIVE; // Replication is not active

    if (shouldThrowError) throwError('Questionnaire field templates not found', 'questionnaireFieldTemplate');
  }, [questionnaireFieldTemplates, questionnaireTemplateId, markAsFound, throwError]);

  /**
   * Check if the questionnaire responses are set after the given interval
   * If the replication is not active or completed, it will throw an error
   * We can't check by the length of the questionnaire responses because it's possible that a questionnaire don't have any responses
   * and the replication is completed
   */
  const checkQuestionnaireResponses = useCallback(async (): Promise<void> => {
    if (questionnaireResponses && questionnaireResponses.length > 0) {
      markAsFound(questionnaireResponses, 'questionnaireResponse');
      return;
    }

    const replicationIdentifier = getQuestionnaireResponseReplicationIdentifier(questionnaireId);
    const replicationData = await getReplicationDataByIdentifier(replicationIdentifier);
    const status = replicationData?.info.status;

    const shouldThrowError =
      !replicationData || // Replication data is not set
      (status !== REPLICATION_INFO_STATUS.COMPLETED && status !== REPLICATION_INFO_STATUS.ACTIVE); // Replication is not active or completed

    if (shouldThrowError) throwError('Questionnaire responses not found', 'questionnaireResponse');
  }, [questionnaireResponses, questionnaireId, markAsFound, throwError]);

  /**
   * Checks for each collection if we want to check if the data is set (value !== false)
   * If we want to check, it will call the corresponding function to check if the data is set
   */
  const checkQuestionnaireData = useCallback(async () => {
    rxdbLog('useCheckErrorsInQuestionnaireRequests - checking questionnaire data');

    if (questionnaireTemplateId) {
      if (questionnaireTemplate !== false) checkQuestionnaireTemplate();
      if (questionnaireFieldTemplates !== false) checkQuestionnaireFieldTemplates();
    }

    if (questionnaireId) {
      if (questionnaireResponses !== false) checkQuestionnaireResponses();
    }
  }, [
    questionnaireId,
    questionnaireTemplateId,
    questionnaireTemplate,
    questionnaireFieldTemplates,
    questionnaireResponses,
    checkQuestionnaireTemplate,
    checkQuestionnaireFieldTemplates,
    checkQuestionnaireResponses,
  ]);

  /**
   * Checks if all collections are added to the successfullyLoaded state
   * If all collections are added, it will cancel the interval
   */
  const checkVerifiedData = useCallback((): void => {
    const allCollectionsAreAdded = Object.keys(successfullyLoaded).length === Object.keys(QuestionnaireCollections).length;
    const allSuccessfullyLoaded = Object.values(successfullyLoaded).every(value => value);

    if (!allCollectionsAreAdded) return;

    rxdbLog('useCheckErrorsInQuestionnaireRequests - all collections were checked, canceling interval.', successfullyLoaded);
    cancelInterval();

    if (allSuccessfullyLoaded && loadTimeoutTimer.current) {
      rxdbLog('useCheckErrorsInQuestionnaireRequests - all collections were successfully loaded, canceling timeout timer.');
      clearTimeout(loadTimeoutTimer.current);
      loadTimeoutTimer.current = null;
    }
  }, [cancelInterval, successfullyLoaded]);

  const checkLoadTimeout = useCallback((): void => {
    if (loadTimeoutTimer.current) clearTimeout(loadTimeoutTimer.current);

    loadTimeoutTimer.current = setTimeout(() => {
      reportTimeoutError.current?.();
    }, LOAD_TIMEOUT);
  }, []);

  useEffect(() => {
    if (intervalCanceled.current) return;

    if (intervalTimer.current) clearInterval(intervalTimer.current);
    intervalTimer.current = setInterval(checkQuestionnaireData, CHECK_INTERVAL);
  }, [checkQuestionnaireData]);

  useEffect(() => {
    checkVerifiedData();
  }, [checkVerifiedData]);

  useEffect(() => {
    checkLoadTimeout();
  }, [checkLoadTimeout]);

  return { errors, successfullyLoaded };
};

export default useCheckErrorsInQuestionnaireRequests;
